import { useRef, useState, useLayoutEffect } from 'react';
import { sanitizeNumericInput, evaluateFormula, looksLikeFormula } from '../utils/validation.js';

function formatCommas(val) {
  const n = parseFloat(String(val).replace(/,/g, ''));
  if (isNaN(n)) return '';
  return n % 1 === 0
    ? n.toLocaleString('en-US')
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Group the integer part of a sanitized in-progress value ("1234.5", "-1234.",
// ".5") with thousands commas while leaving the typed decimal part untouched,
// so partially-typed decimals survive live formatting.
function formatLive(cleaned) {
  if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return cleaned;
  const negative = cleaned.startsWith('-');
  const body = negative ? cleaned.slice(1) : cleaned;
  const dot = body.indexOf('.');
  const intPart = dot === -1 ? body : body.slice(0, dot);
  const rest = dot === -1 ? '' : body.slice(dot);
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (negative ? '-' : '') + grouped + rest;
}

export default function CurrencyInput({ value, onChange, ...rest }) {
  const inputRef = useRef(null);
  // Where the caret belongs after React re-renders the formatted value; set
  // during handleChange, consumed (and cleared) by the layout effect below.
  const caretRef = useRef(null);
  // The exact string being edited while the field has focus. Displaying this
  // (instead of re-deriving from the stored number) is what lets in-progress
  // states like "1,234." keep their trailing dot.
  const [editing, setEditing] = useState(null);

  const display = editing != null
    ? editing
    : value === '' || value == null ? '' : formatCommas(value);

  useLayoutEffect(() => {
    if (caretRef.current == null || !inputRef.current) return;
    inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
    caretRef.current = null;
  });

  // Commit a typed expression ("=8400*12", or just "8400*12") as its result.
  // Called on blur and on Enter. A malformed expression is left alone rather
  // than silently zeroing the field, so the advisor can see and fix it.
  const commitFormula = () => {
    if (editing == null || !looksLikeFormula(editing)) return false;
    const result = evaluateFormula(editing);
    if (result == null) return false;
    setEditing(null);
    onChange(result);
    return true;
  };

  const handleChange = e => {
    const el = e.target;
    const rawCaret = el.selectionStart ?? el.value.length;

    // While the text reads as an expression, pass it through untouched: the
    // usual sanitizer strips + - * / ( ) on every keystroke, which is why
    // typing a formula was impossible. Nothing is stored until it resolves.
    if (looksLikeFormula(el.value)) {
      setEditing(el.value);
      caretRef.current = rawCaret;
      return;
    }
    // Count the characters before the caret that survive sanitizing; the caret
    // goes after that many surviving characters in the reformatted string, so
    // inserted/removed commas (or stripped "$"/letters) never displace it.
    const keptBefore = el.value.slice(0, rawCaret).replace(/[^0-9.\-]/g, '').length;

    // Strip currency symbols, commas, and stray letters BEFORE storing so a
    // value like "$1,000 abc" becomes 1000 instead of being kept as garbage
    // (which formatted blank and computed as $0, silently zeroing the asset).
    const cleaned = sanitizeNumericInput(el.value);
    const formatted = formatLive(cleaned);

    let pos = 0;
    let seen = 0;
    while (pos < formatted.length && seen < keptBefore) {
      if (formatted[pos] !== ',') seen += 1;
      pos += 1;
    }
    caretRef.current = pos;
    setEditing(formatted);

    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') {
      onChange(cleaned === '' ? '' : cleaned);
      return;
    }
    const n = parseFloat(cleaned);
    onChange(isNaN(n) ? cleaned : n);
  };

  return (
    <span className="currency-input">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={display}
        // Hover shows the full formatted amount — narrow table columns can clip
        // a 7-figure value, and $1,000,000 misread as $100,000 is a real risk.
        title={display ? `$${display}` : undefined}
        onFocus={() => setEditing(formatLive(sanitizeNumericInput(String(value ?? ''))))}
        onBlur={() => { if (!commitFormula()) setEditing(null); }}
        onKeyDown={e => {
          if (e.key === 'Enter' && commitFormula()) e.preventDefault();
        }}
        onChange={handleChange}
        {...rest}
      />
    </span>
  );
}
